import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { Colors } from '@/constants/colors';

interface StockHeaderProps {
  ticker: string;
  companyName: string;
  price?: number;
  change?: number;
  changePercent?: number;
  isLoading?: boolean;
}

export const StockHeader: React.FC<StockHeaderProps> = ({
  ticker,
  companyName,
  price,
  change,
  changePercent,
  isLoading = false,
}) => {
  const isPositive = (change ?? 0) >= 0;

  return (
    <View style={styles.container}>
      <View style={styles.titleRow}>
        <Text style={styles.ticker}>{ticker}</Text>
        <Text style={styles.dot}>·</Text>
        <Text style={styles.companyName} numberOfLines={1}>
          {companyName}
        </Text>
      </View>

      {isLoading ? (
        <View style={styles.priceRow}>
          <ActivityIndicator size="small" color={Colors.text.secondary} />
          <Text style={styles.loadingText}>Loading price...</Text>
        </View>
      ) : price !== undefined ? (
        <View style={styles.priceRow}>
          <Text style={styles.price}>${price.toFixed(2)}</Text>
          {change !== undefined && changePercent !== undefined && (
            <View
              style={[styles.changeBadge, isPositive ? styles.positiveBadge : styles.negativeBadge]}
            >
              <Text style={styles.changeText}>
                {isPositive ? '+' : ''}
                {change.toFixed(2)} ({isPositive ? '+' : ''}
                {changePercent.toFixed(2)}%)
              </Text>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Colors.background.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  ticker: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  dot: {
    fontSize: 16,
    color: Colors.text.tertiary,
    marginHorizontal: 8,
  },
  companyName: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.text.secondary,
    flex: 1,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  price: {
    fontSize: 28,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  changeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  positiveBadge: {
    backgroundColor: 'rgba(52, 199, 89, 0.15)',
  },
  negativeBadge: {
    backgroundColor: 'rgba(255, 59, 48, 0.15)',
  },
  changeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.tertiary,
    marginLeft: 8,
  },
});
